/**
 * `?ui=<name>` (08 intro, 06 §8): every menu screen on its own, on made-up data - no storage, no
 * profile, no mission. It mounts the real menus on the in-memory `GameApi`, so the screen on
 * show is live: its buttons lead on through the same fake world. FLY leads nowhere.
 */

import type { Deployment } from '../../../meta/api';
import { trainingDeployment } from '../../../meta/training';
import type { Aftermath } from '../../../meta/types';
import type { Ctx, MissionPlan } from '../context';
import { mountMenus } from '../menusCore';
import { SettingsStore } from '../settings';
import { notice } from '../widgets/confirm';
import { missionResult, params, ranger } from './data';
import type { FakeApi, FakeOptions } from './fakeApi';
import { createFakeApi } from './fakeApi';
import { createFakeBindings } from './fakeBindings';
import { createFakeCloud } from './fakeCloud';

function operationPlan(api: FakeApi, rangerId: string | null): MissionPlan {
  const node = api.state().operation?.nodes.find((n) => n.id === 'n0b');
  if (node === undefined || node.params === null) throw new Error('the fixture operation has no node n0b');
  return { mode: 'operation', params: node.params, node, rangerId };
}

function deployment(api: FakeApi, rangerId: string): Deployment {
  const plan = operationPlan(api, rangerId);
  const carried = { magazines: 4, roundsInMag: 30, grenades: 3, laws: 1, bombs: 1, firstAid: 1 };
  return { params: plan.params, rangerId, loadout: { carried, pods: [] }, mods: {}, mode: 'operation' };
}

function aftermath(overrides: Partial<Aftermath>): Aftermath {
  return {
    ranger: ranger('r1', { firstName: 'Dale', surname: 'Marlowe', callsign: 'Viper', rank: 1, merit: 2278 }),
    meritGained: 2278,
    rpGained: 14,
    promotedTo: null,
    perkChoices: null,
    medals: [],
    readinessChange: 0,
    readinessReasons: [],
    freedRangers: [],
    memorial: null,
    operationStatus: 'active',
    ...overrides,
  };
}

interface Fixture {
  options?: FakeOptions;
  /** Prepare the fake world before the screen reads it. */
  setup?(api: FakeApi): void;
  go(ctx: Ctx, api: FakeApi): void;
}

const FIXTURES: Readonly<Record<string, Fixture>> = {
  title: { go: (ctx) => ctx.nav.title() },
  newOperation: { options: { withOperation: false }, go: (ctx) => ctx.nav.newOperation() },
  company: { go: (ctx) => ctx.nav.formCompany() },
  personnel: { go: (ctx) => ctx.nav.personnel() },
  theatre: { go: (ctx) => ctx.nav.theatre() },
  briefing: {
    // The order on show is one of the expansion's (10 §2): a mission item in pod A, a stated time, a quiet rule.
    setup: (api) => {
      const node = api.state().operation?.nodes.find((n) => n.id === 'n0b');
      if (node !== undefined) {
        node.params = params('beacon', 'arctic', { threat: 5, modifiers: ['night-op'], timeOfDay: 'night' });
        node.variant = false;
      }
    },
    go: (ctx, api) => ctx.nav.briefing(operationPlan(api, null)),
  },
  rangers: { go: (ctx, api) => ctx.nav.rangerSelect(operationPlan(api, null)) },
  loadout: { go: (ctx, api) => ctx.nav.loadout(operationPlan(api, 'r2')) },
  'aftermath-success': {
    go: (ctx, api) =>
      ctx.nav.aftermath({
        deployment: deployment(api, 'r1'),
        result: missionResult(),
        aftermath: aftermath({
          promotedTo: 1,
          perkChoices: ['ghost', 'sapper', 'deadeye'],
          medals: ['commendation-1', 'bronze-star'],
          readinessChange: -1,
          readinessReasons: ['MISSION ATTEMPTED', 'EXTRACTED QUIET'],
          tagRecovery: { name: 'Joss Hale', perk: 'knife-work' },
        }),
      }),
  },
  'aftermath-kia': {
    go: (ctx, api) => {
      const fallen = ranger('r3', { firstName: 'Tomas', surname: 'Reyes', callsign: 'Brick', rank: 2, status: 'kia' });
      ctx.nav.aftermath({
        deployment: deployment(api, 'r3'),
        result: missionResult({ outcome: 'kia', objectiveComplete: false, total: 310, multiplier: 1 }),
        aftermath: aftermath({
          ranger: fallen,
          meritGained: 310,
          rpGained: 0,
          medals: ['purple-heart'],
          readinessChange: 2,
          readinessReasons: ['MISSION ATTEMPTED', 'MISSION FAILED'],
          memorial: {
            ranger: fallen,
            fate: 'kia',
            operation: 'IRON LANTERN',
            mission: 'Destroy a Munitions Depot',
            cause: 'KILLED BY ENEMY FIRE',
            epitaph: 'He went in first. He always did.',
          },
        }),
      });
    },
  },
  // ROUND7 §5: the training course's end card, on a course finished with the wall down.
  training: {
    go: (ctx) =>
      ctx.nav.aftermath({
        deployment: trainingDeployment(),
        result: missionResult({ outcome: 'extracted', objectiveComplete: true, lines: [], total: 0, multiplier: 1 }),
        aftermath: null,
      }),
  },
  operationEnd: {
    setup: (api) => {
      const op = api.state().operation;
      if (op !== null) op.status = 'won';
    },
    go: (ctx) => ctx.nav.operationEnd(),
  },
  hall: { go: (ctx) => ctx.nav.hall() },
  memorial: { go: (ctx) => ctx.nav.memorial() },
  records: { go: (ctx) => ctx.nav.records() },
  daily: { go: (ctx) => ctx.nav.daily() },
  classic: { go: (ctx) => ctx.nav.classic() },
  practice: { go: (ctx) => ctx.nav.practice() },
  options: { go: (ctx) => ctx.nav.options() },
  controls: { go: (ctx) => ctx.nav.controls() },
  cloud: { go: (ctx) => ctx.nav.cloud() },
  credits: { go: (ctx) => ctx.nav.credits() },
  changes: { go: (ctx) => ctx.nav.changes() },
};

/** Every name `showFixture` answers to. */
export const FIXTURE_NAMES: readonly string[] = Object.keys(FIXTURES);

/**
 * Mounts ONE menu screen on fixture data under `root` and returns true; false for a name it does
 * not know (nothing is mounted). Settings live in memory only, so nothing the visitor changes
 * here follows him into the game.
 */
export function showFixture(root: HTMLElement, name: string): boolean {
  const fixture = Object.prototype.hasOwnProperty.call(FIXTURES, name) ? FIXTURES[name] : undefined;
  if (fixture === undefined) return false;
  const api = createFakeApi({ showcase: true, ...fixture.options });
  fixture.setup?.(api);
  const mounted = mountMenus({
    root,
    api,
    settings: new SettingsStore(null),
    onFly: () => {
      // There is no mission behind a fixture: put the menus back and say so.
      mounted.setVisible(true);
      fixture.go(mounted.ctx, api);
      const lines = ['THIS IS WHERE THE MISSION WOULD BEGIN. A ?UI= SCREEN HAS NONE BEHIND IT.'];
      notice(mounted.ctx, 'FIXTURE ROUTE', lines);
    },
    suspended: () => null,
    ports: { bindings: createFakeBindings(), cloud: createFakeCloud() },
  });
  mounted.setVisible(true);
  fixture.go(mounted.ctx, api);
  return true;
}
